import { API_BASE_URL } from '../config/api';

export interface Trade {
  id: string;
  asset: string;
  type: string;
  entry: string;
  size: string;
  pnl: string;
  percent: string;
  duration: string;
  positive: boolean;
}

export interface DailyPerformance {
  date: string;
  dayName: string;
  realized: string;
  realizedValue: number;
  tradesCount: number;
  positive: boolean;
  neutral: boolean;
}

export interface PortfolioData {
  totalBalance: string;
  netPnl: string;
  netPnlPercent: string;
  netPnlPositive: boolean;
  dayWinRate: string;
  dayWinRateSub: string;
  tradeWinRate: string;
  tradeWinRateSub: string;
  avgWinLoss: string;
  rrRatio: string;
  activeDeployments: Trade[];
  performance: DailyPerformance[];
  returnOnLong?: string;
  returnOnShort?: string;
  highcapPnl?: string;
  midcapPnl?: string;
  lowcapPnl?: string;
}

// Simulated network delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const apiService = {
  /**
   * Connects to a broker API via the Laravel backend proxy.
   * Uses specific endpoint per exchange for better clarity and debugging.
   */
  async connectBrokerAPI(apiKey: string, apiSecret: string, brokerId: string, apiPassphrase?: string): Promise<PortfolioData> {
    if (!apiKey || !apiSecret) {
      throw new Error("Invalid API keys provided.");
    }

    // Map broker ID to specific endpoint
    const endpointMap: Record<string, string> = {
      'binance': `${API_BASE_URL}/api/sync/binance`,
      'bybit': `${API_BASE_URL}/api/sync/bybit`,
      'okx': `${API_BASE_URL}/api/sync/okx`,
      'mexc': `${API_BASE_URL}/api/sync/mexc`,
      'bitget': `${API_BASE_URL}/api/sync/bitget`,
      'indodax': `${API_BASE_URL}/api/sync/indodax`,
      'tokocrypto': `${API_BASE_URL}/api/sync/tokocrypto`,
    };

    const endpoint = endpointMap[brokerId.toLowerCase()];
    
    if (!endpoint) {
      throw new Error(`Exchange "${brokerId}" is not supported yet. Currently supported: Binance, Bybit, OKX, MEXC, Bitget, Indodax, Tokocrypto.`);
    }

    const payload: any = {
      api_key: apiKey,
      api_secret: apiSecret
    };

    if (apiPassphrase) {
      payload.api_passphrase = apiPassphrase;
    }

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Failed to sync with ${brokerId}.`);
    }

    const resJson = await response.json();
    return resJson.data;
  },

  async login(email: string, password: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Login failed.');
    }

    return await response.json();
  },

  async loginWithGoogle(token: string, referralCode?: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/login/google`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ token, referral_code: referralCode })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Google Sign-in failed.');
    }

    return await response.json();
  },

  async loginWithFirebase(token: string, referralCode?: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/login/firebase`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ token, referral_code: referralCode })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Firebase authentication failed.');
    }

    return await response.json();
  },

  /**
   * Get journal entries for a specific month
   */
  async getJournals(year: number, month: number, token: string, cexAccountId?: number): Promise<any> {
    let url = `${API_BASE_URL}/api/journals?year=${year}&month=${month}`;
    if (cexAccountId) {
      url += `&cex_account_id=${cexAccountId}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch journals.');
    }

    return await response.json();
  },

  /**
   * Get journal entry for a specific date
   */
  async getJournal(date: string, token: string, cexAccountId?: number): Promise<any> {
    let url = `${API_BASE_URL}/api/journal?date=${date}`;
    if (cexAccountId) {
      url += `&cex_account_id=${cexAccountId}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch journal.');
    }

    return await response.json();
  },

  /**
   * Save journal entry
   */
  async saveJournal(data: {
    cex_account_id: number;
    trade_date: string;
    remarks?: string;
    screenshots?: string[];
    mood?: string;
    pnl?: number;
    trades_count?: number;
  }, token: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/journal`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to save journal.');
    }

    return await response.json();
  },

  /**
   * Delete journal entry
   */
  async deleteJournal(date: string, token: string, cexAccountId?: number): Promise<any> {
    let url = `${API_BASE_URL}/api/journal?date=${date}`;
    if (cexAccountId) {
      url += `&cex_account_id=${cexAccountId}`;
    }

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to delete journal.');
    }

    return await response.json();
  },

  /**
   * Store a manual trade (multi-execution) via storeManual endpoint
   */
  async storeManualTrade(payload: {
    pair: string;
    executions: {
      cex_account_id: number;
      trade_date: string;
      side: 'BUY' | 'SELL';
      price: number;
      qty: number;
      commission?: number;
      fee?: number;
    }[];
  }, token: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/trades/manual`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to store manual trade.');
    }

    return await response.json();
  },

  /**
   * Get trade autocomplete suggestions for a given pair
   */
  async getTradeSuggestions(pair: string, token: string, cexAccountId?: number): Promise<any> {
    let url = `${API_BASE_URL}/api/trades/suggestions?pair=${encodeURIComponent(pair)}`;
    if (cexAccountId) {
      url += `&cex_account_id=${cexAccountId}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) return { success: true, data: [] };
    return await response.json();
  },

  /**
   * Update an existing trade (remarks, session, setup type, etc.)
   */
  async updateTrade(tradeId: number, data: Record<string, any>, token: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/trades/${tradeId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update trade.');
    }

    return await response.json();
  },

  /**
   * Get hold time analysis for Reports page
   */
  async getHoldTimeAnalysis(token: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/trades/hold-time-analysis`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch hold time analysis.');
    }

    return await response.json();
  }
};
